/**
 * Contact Picker Context and Hook
 * Provides Contact Picker API availability state throughout the React component tree
 */

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import { IonActionSheet, IonButton, useIonRouter } from '@ionic/react';
import { useDeviceInfo } from '@/hooks/useDeviceInfo';
import { useAppToast } from '@/hooks/useAppToast';
import { ContactLogic, type Contact } from '@/modules/contact';
import {
  pxToRem,
  footerControlRoundButtonStyle,
} from '@/modules/config/constants';

/**
 * The parsed result of a device contact-picker selection, handed to a
 * DeviceContactPicker's `onSelect` callback. Callers decide how to ingest the
 * contacts (e.g. add to the address book, or add as share recipients) and how
 * to surface the skipped-entry counts.
 */
export interface DeviceContactSelection {
  contacts: Contact[];
  noSmsableCount: number;
  droppedPhoneCount: number;
}

interface DeviceContactPickerProps {
  /** Called with the parsed selection once the user picks device contacts. */
  onSelect: (selection: DeviceContactSelection) => void;
  /** When true, the button is non-interactive and visually dimmed. */
  disabled?: boolean;
}

interface ContactPickerContextType {
  isAvailable: boolean;
  canBeEnabled: boolean;
  recheckSupport: () => void;
  /**
   * Self-contained "Device Contact" picker button. Renders nothing unless the
   * native picker is available or can be enabled; opens the native picker (or
   * the iOS enable-it help sheet) and hands the parsed selection to `onSelect`.
   */
  DeviceContactPicker: React.FC<DeviceContactPickerProps>;
}

const ContactPickerContext = createContext<
  ContactPickerContextType | undefined
>(undefined);

interface ContactPickerProviderProps {
  children: React.ReactNode;
}

/**
 * Detects if the Contact Picker API is available
 */
const checkContactPickerAvailability = (): boolean => {
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const anyNav = navigator as any;
    return !!(
      anyNav &&
      anyNav.contacts &&
      typeof anyNav.contacts.select === 'function'
    );
  } catch {
    return false;
  }
};

export const useContactPicker = (): ContactPickerContextType => {
  const context = useContext(ContactPickerContext);
  if (context === undefined) {
    throw new Error(
      'useContactPicker must be used within a ContactPickerProvider'
    );
  }
  return context;
};

/**
 * The "Device Contact" pill button shared by the address book and the share
 * wizard. Encapsulates availability gating, the native picker call + parse, the
 * access-error toast, and the iOS "enable it in settings" help sheet — so call
 * sites only supply an `onSelect` ingestion handler.
 */
export const DeviceContactPicker: React.FC<DeviceContactPickerProps> = ({
  onSelect,
  disabled = false,
}) => {
  const { isAvailable, canBeEnabled } = useContactPicker();
  const router = useIonRouter();
  const [presentToast] = useAppToast();
  const [showHelpSheet, setShowHelpSheet] = useState(false);

  // Render nothing when the device can neither pick nor be enabled to pick.
  if (!isAvailable && !canBeEnabled) {
    return null;
  }

  const handleClick = async () => {
    if (disabled) return;

    if (!isAvailable) {
      // Supported-but-not-enabled (iOS): offer to enable it in settings.
      setShowHelpSheet(true);
      return;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const anyNav = navigator as any;
    let results;
    try {
      results = await anyNav.contacts.select(['name', 'tel'], {
        multiple: true,
      });
    } catch (e) {
      if (e instanceof Error && e.name === 'AbortError') {
        return;
      }
      presentToast({
        message: 'Unable to access contacts. Please try again.',
        duration: 3000,
        color: 'warning',
      });
      return;
    }

    onSelect(ContactLogic.parseDeviceContactSelection(results || []));
  };

  return (
    <>
      <IonButton
        fill="clear"
        onClick={handleClick}
        aria-label="Add device contact"
        data-testid="device-contact-picker-button"
        disabled={disabled}
        className="footer-glass-button footer-glass-round"
        style={
          {
            ...footerControlRoundButtonStyle,
            height: 44,
            minWidth: 100,
            '--border-radius': '22px',
            '--padding-start': '12px',
            '--padding-end': '12px',
            flex: '0 0 auto',
          } as React.CSSProperties
        }
      >
        <span
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 8,
          }}
        >
          <span
            style={{ fontSize: pxToRem(17), lineHeight: 1, fontWeight: 500 }}
          >
            +
          </span>
          <span
            style={{
              fontSize: pxToRem(12),
              lineHeight: pxToRem(13),
              fontWeight: 600,
              textAlign: 'left',
            }}
          >
            Device
            <br />
            Contact
          </span>
        </span>
      </IonButton>
      <IonActionSheet
        isOpen={showHelpSheet}
        onDidDismiss={() => setShowHelpSheet(false)}
        subHeader="Native contact picking may be available on this device, but it needs to be enabled first."
        buttons={[
          {
            text: 'Enable',
            handler: () => {
              router.push('/settings/contacts/picker', 'forward', 'push');
            },
          },
          {
            text: 'Cancel',
            role: 'cancel',
          },
        ]}
      />
    </>
  );
};

export const ContactPickerProvider: React.FC<ContactPickerProviderProps> = ({
  children,
}) => {
  const { isIOS } = useDeviceInfo();
  const [isAvailable, setIsAvailable] = useState(
    checkContactPickerAvailability
  );

  // Manual recheck function
  const recheckSupport = useCallback(() => {
    const avail = checkContactPickerAvailability();
    setIsAvailable(avail);
    // eslint-disable-next-line no-console
    console.log('Contact Picker support re-checked:', avail);
  }, []);

  // Keep a stable reference to the handler
  const recheckSupportRef = useRef(recheckSupport);
  recheckSupportRef.current = recheckSupport;

  // Listen for global event to recheck support
  useEffect(() => {
    const handleRecheck = () => {
      recheckSupportRef.current();
    };

    window.addEventListener('recheckContactPicker', handleRecheck);
    return () => {
      window.removeEventListener('recheckContactPicker', handleRecheck);
    };
  }, []);

  const value: ContactPickerContextType = useMemo(
    () => ({
      isAvailable,
      canBeEnabled: !isAvailable && isIOS && window.isSecureContext,
      recheckSupport,
      DeviceContactPicker,
    }),
    [isAvailable, isIOS, recheckSupport]
  );

  return (
    <ContactPickerContext.Provider value={value}>
      {children}
    </ContactPickerContext.Provider>
  );
};
